import React, { useState, useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '@core/hooks/useNotifications';
import { fastLanerService } from '@/services';
import { ApkList } from './';
import { ApkListRef } from './types';

interface UploadProps {
    fastLanerOrgId: string | null;
    onLoading?: () => void;
    onLoaded?: () => void;
}

export interface UploadRef {
    load: () => void;
}

const Upload = forwardRef<UploadRef, UploadProps>(({ fastLanerOrgId, onLoading, onLoaded }, ref) => {
    const { t } = useTranslation();
    const { notifySuccess, notifyError, notifyErrorResponse } = useNotifications();
    const [uploading, setUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const apkListRef = useRef<ApkListRef>(null);

    const allowedType = 'application/vnd.android.package-archive';
    const allowedExtension = 'apk';
    const allowedMaxSizeBytes = 256 * 1024 * 1024; // 256 MB

    useImperativeHandle(ref, () => ({
        load: () => {
            apkListRef.current?.load();
        }
    }));

    const isValidFileType = useCallback(
        (file: File): boolean => {
            if (file.type === allowedType) {
                return true;
            }

            const fileName = file.name;
            const fileExtension = fileName.split('.').pop()?.toLowerCase();
            return fileExtension === allowedExtension;
        },
        [allowedType, allowedExtension]
    );

    const uploadFile = useCallback(
        async (file: File) => {
            if (!fastLanerOrgId) return;

            setUploading(true);
            const formData = new FormData();
            formData.append('file', file);

            try {
                await fastLanerService.uploadFastlanerApk(fastLanerOrgId, formData);
                notifySuccess();
            } catch (error) {
                notifyErrorResponse(error);
            } finally {
                apkListRef.current?.load();
                setUploading(false);
                setSelectedFile(null);
            }
        },
        [fastLanerOrgId]
    );

    const handleFileSelect = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (!file) return;

            if (file.size <= 0) {
                notifyError(t('fileIsEmpty'));
                setSelectedFile(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                return;
            }

            if (file.size > allowedMaxSizeBytes) {
                notifyError(t('fileIsTooLarge'));
                setSelectedFile(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                return;
            }

            if (!isValidFileType(file)) {
                notifyError(t('onlyApkSupported'));
                setSelectedFile(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                return;
            }

            setSelectedFile(file);
            uploadFile(file);
        },
        [isValidFileType, uploadFile]
    );

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    }, []);

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setDragActive(false);

            const file = e.dataTransfer.files?.[0];
            if (!file) return;

            if (file.size <= 0) {
                notifyError(t('fileIsEmpty'));
                return;
            }

            if (file.size > allowedMaxSizeBytes) {
                notifyError(t('fileIsTooLarge'));
                return;
            }

            if (!isValidFileType(file)) {
                notifyError(t('onlyApkSupported'));
                return;
            }

            setSelectedFile(file);
            uploadFile(file);
        },
        [isValidFileType, uploadFile, allowedMaxSizeBytes]
    );

    const handleClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    return (
        <Box  sx={{ py: 3 }}>
            <Box sx={{ mb: 3 }}>
                <ApkList ref={apkListRef} fastLanerOrgId={fastLanerOrgId} onLoading={onLoading} onLoaded={onLoaded} />
            </Box>

            <Card
                sx={{
                    opacity: uploading || !fastLanerOrgId ? 0.6 : 1,
                    pointerEvents: uploading || !fastLanerOrgId ? 'none' : 'auto'
                }}
            >
                <CardContent>
                    <Box
                        onClick={handleClick}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        sx={{
                            padding: 3,
                            border: '1px dashed',
                            borderColor: dragActive ? 'primary.main' : 'divider',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'background-color 0.3s ease, border-color 0.3s ease',
                            backgroundColor: dragActive ? 'action.hover' : 'transparent',
                            '&:hover': {
                                backgroundColor: 'action.hover'
                            }
                        }}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept={`.${allowedExtension}`}
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                        />
                        <CloudUploadIcon sx={{ mr: 1, fontSize: 32 }} />
                        <Typography variant="body1">
                            {selectedFile ? `${t('uploading')} ${selectedFile.name}..` : t('dragAndDropOrClickUpload')}
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
});

Upload.displayName = 'Upload';

export default Upload;
